import { getTileUrl, getTileAttribution } from "../../utils/map-utils";
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, Alert, Skeleton, Grid, Chip, Select, MenuItem,
  FormControl, InputLabel, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Collapse,
  SelectChangeEvent, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Warning as WarningIcon,
  LocationOn as LocationOnIcon,
  AddBusiness as AddBusinessIcon,
  OpenInNew as OpenInNewIcon,
  Whatshot as WhatshotIcon,
  NotListedLocation as NotListedLocationIcon,
  Gavel as GavelIcon,
  LayersClear as LayersClearIcon,
} from '@mui/icons-material';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Legend } from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { monitoramentoService } from '../../services/api';
import KpiCard from '../../components/monitoramento/KpiCard';
import { ChatIAButton } from '../../components/ChatIA';
import AnalisePredicaoCard from '../../components/saude/AnalisePredicaoCard';
import RegistroIntegradoModal from '../../components/monitoramento/RegistroIntegradoModal';
import AnaliseRiscoSection from '../../components/monitoramento/AnaliseRiscoSection';

// Leaflet
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { MapContainer, TileLayer, Marker, Popup, Tooltip as LeafletTooltip, useMap } from 'react-leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import 'leaflet.heat';

// Fix leaflet default icon URLs
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({ iconRetinaUrl: markerIcon2x, iconUrl: markerIcon, shadowUrl: markerShadow });

// ---------- CONSTANTS ----------

const CORES_RISCO: Record<string, string> = {
  CRITICO: '#E74C3C',
  ALTO: '#F39C12',
  MEDIO: '#F1C40F',
  BAIXO: '#27AE60',
};

const CORES_SITUACAO: Record<string, string> = {
  REGULAR: '#27AE60',
  IRREGULAR: '#F39C12',
  INTERDITADO: '#E74C3C',
  FECHADO: '#95A5A6',
};

const CORES_PIE = ['#1E3A5F', '#2D7D9A', '#27AE60', '#F39C12', '#E74C3C', '#8E44AD', '#E67E22', '#16A085'];

const ALERTA_LABELS: Record<string, string> = {
  ALVARA_VENCENDO: 'Alvara Vencendo',
  ALVARA_VENCIDO: 'Alvara Vencido',
  MEDIDA_CAUTELAR: 'Medida Cautelar',
  INSPECAO_ATRASADA: 'Inspecao Atrasada',
  SEM_ALVARA: 'Sem Alvara',
};

const ALERTA_COLORS: Record<string, string> = {
  ALVARA_VENCENDO: '#F39C12',
  ALVARA_VENCIDO: '#E74C3C',
  MEDIDA_CAUTELAR: '#8E44AD',
  INSPECAO_ATRASADA: '#E67E22',
  SEM_ALVARA: '#95A5A6',
};

// ---------- ICONS ----------

function makeIcon(color: string) {
  return new L.DivIcon({
    className: '',
    html: `<div style="width:24px;height:24px;background:${color};border:3px solid #fff;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 2px 6px rgba(0,0,0,0.35);"></div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 24],
    popupAnchor: [0, -24],
  });
}

const ICONS_RISCO: Record<string, L.DivIcon> = {
  CRITICO: makeIcon(CORES_RISCO.CRITICO),
  ALTO: makeIcon(CORES_RISCO.ALTO),
  MEDIO: makeIcon(CORES_RISCO.MEDIO),
  BAIXO: makeIcon(CORES_RISCO.BAIXO),
};

// ---------- HEATMAP LAYER ----------

const HEATMAP_GRADIENTS: Record<string, Record<number, string>> = {
  alto_risco:  { 0.2: '#FFEDA0', 0.5: '#FD8D3C', 0.8: '#E31A1C', 1.0: '#800026' },
  sem_licenca: { 0.2: '#D6E4F5', 0.5: '#6BAED6', 0.8: '#2171B5', 1.0: '#08306B' },
  medidas:     { 0.2: '#F2D7EE', 0.5: '#C994C7', 0.8: '#993404', 1.0: '#67000D' },
};

function HeatmapLayer({ points, modo }: { points: [number, number, number][]; modo: string }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    const gradient = HEATMAP_GRADIENTS[modo] || HEATMAP_GRADIENTS.alto_risco;
    const heat = (L as any).heatLayer(points, {
      radius: 28, blur: 18, maxZoom: 17, max: 4.0, gradient,
    }).addTo(map);
    return () => { map.removeLayer(heat); };
  }, [map, points, modo]);
  return null;
}

// ---------- MAIN COMPONENT ----------

interface Estabelecimento {
  id: number; cnpj: string; razao_social: string; nome_fantasia: string;
  atividade_principal: string; setor: string; endereco: string; bairro: string;
  regional?: string;
  latitude: number; longitude: number; alvara_numero: string;
  alvara_emissao: string; alvara_validade: string; alvara_status: string;
  nivel_risco: string; ultima_inspecao: string; proxima_inspecao: string;
  resultado_ultima_inspecao: string; medida_cautelar: boolean;
  medida_cautelar_tipo: string; medida_cautelar_data: string; situacao: string;
}

type HeatmapModo = 'off' | 'alto_risco' | 'sem_licenca' | 'medidas';

const REGIONAIS_CONTAGEM = ['Sede','Eldorado','Industrial','Nacional','Petrolandia','Ressaca','Riacho','Vargem das Flores'];

interface AlertaRow {
  tipo_alerta: string; razao_social: string; nome_fantasia: string;
  bairro: string; data_referencia: string; nivel_risco: string; situacao: string;
}

const MonitoramentoVisaGeo: React.FC = () => {
  const { municipioAtivo } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [estabelecimentos, setEstabelecimentos] = useState<Estabelecimento[]>([]);
  const [analise, setAnalise] = useState<any>(null);
  const [alertas, setAlertas] = useState<AlertaRow[]>([]);
  const [heatmapData, setHeatmapData] = useState<[number, number, number][]>([]);
  const [heatmapModo, setHeatmapModo] = useState<HeatmapModo>('off');
  const [showFilters, setShowFilters] = useState(false);

  // RF-16.4: analise avancada
  const [analiseAvancada, setAnaliseAvancada] = useState<any>(null);

  // RF-16.2: modal Registro Integrado
  const [registroAbertoId, setRegistroAbertoId] = useState<number | null>(null);

  // Filters
  const [filterSetor, setFilterSetor] = useState('');
  const [filterRisco, setFilterRisco] = useState('');
  const [filterAlvara, setFilterAlvara] = useState('');
  const [filterBairro, setFilterBairro] = useState('');
  const [filterRegional, setFilterRegional] = useState('');
  const [filterMedida, setFilterMedida] = useState('');

  const municipioId = municipioAtivo?.id;
  const municipioNome = municipioAtivo?.nome || '';

  const fetchData = useCallback(async () => {
    if (!municipioId) return;
    setLoading(true);
    setError('');
    try {
      const filters: Record<string, string> = {};
      if (filterSetor) filters.setor = filterSetor;
      if (filterRisco) filters.nivel_risco = filterRisco;
      if (filterAlvara) filters.alvara_status = filterAlvara;
      if (filterBairro) filters.bairro = filterBairro;
      if (filterRegional) filters.regional = filterRegional;
      if (filterMedida) filters.medida_cautelar = filterMedida;

      const [estabs, analiseData, alertasData, analiseAv] = await Promise.all([
        monitoramentoService.getVisaGeoEstabelecimentos(municipioId, Object.keys(filters).length ? filters : undefined),
        monitoramentoService.getVisaGeoAnaliseRisco(municipioId),
        monitoramentoService.getVisaGeoAlertas(municipioId),
        monitoramentoService.getVisaGeoAnaliseRiscoAvancada(municipioId),
      ]);
      setEstabelecimentos(estabs || []);
      setAnalise(analiseData || {});
      setAlertas(alertasData || []);
      setAnaliseAvancada(analiseAv || null);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [municipioId, filterSetor, filterRisco, filterAlvara, filterBairro, filterRegional, filterMedida]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Load heatmap when mode changes
  useEffect(() => {
    if (heatmapModo === 'off' || !municipioId) { setHeatmapData([]); return; }
    monitoramentoService.getVisaGeoHeatmap(municipioId, heatmapModo).then((data: any) => {
      setHeatmapData((data || []).map((d: any) => [Number(d.latitude), Number(d.longitude), Number(d.intensidade)]));
    }).catch(() => setHeatmapData([]));
  }, [heatmapModo, municipioId]);

  // Derived data
  const comCoordenadas = useMemo(() =>
    estabelecimentos.filter(e => e.latitude && e.longitude),
    [estabelecimentos]
  );

  const mapCenter = useMemo<[number, number]>(() => {
    if (!comCoordenadas.length) return [-19.93, -44.05];
    const lat = comCoordenadas.reduce((s, e) => s + Number(e.latitude), 0) / comCoordenadas.length;
    const lng = comCoordenadas.reduce((s, e) => s + Number(e.longitude), 0) / comCoordenadas.length;
    return [lat, lng];
  }, [comCoordenadas]);

  const bairros = useMemo(() =>
    Array.from(new Set(estabelecimentos.map(e => e.bairro).filter(Boolean))).sort(),
    [estabelecimentos]
  );

  const kpis = analise?.kpis || {};

  // Charts data
  const chartSetor = useMemo(() => {
    if (!analise?.por_setor) return [];
    const map: Record<string, number> = {};
    analise.por_setor.forEach((r: any) => { map[r.setor] = (map[r.setor] || 0) + Number(r.total); });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [analise]);

  const chartBairro = useMemo(() => {
    if (!analise?.por_bairro) return [];
    const map: Record<string, Record<string, number>> = {};
    analise.por_bairro.forEach((r: any) => {
      if (!map[r.bairro]) map[r.bairro] = {};
      map[r.bairro][r.nivel_risco] = Number(r.total);
    });
    return Object.entries(map)
      .map(([bairro, riscos]) => ({ bairro, ...riscos }))
      .sort((a: any, b: any) => {
        const ta = Object.values(a).filter(v => typeof v === 'number').reduce((s: number, v: any) => s + v, 0);
        const tb = Object.values(b).filter(v => typeof v === 'number').reduce((s: number, v: any) => s + v, 0);
        return (tb as number) - (ta as number);
      })
      .slice(0, 12);
  }, [analise]);

  const chartSituacao = useMemo(() => {
    if (!analise?.por_situacao) return [];
    return analise.por_situacao.map((r: any) => ({ name: r.situacao, value: Number(r.total) }));
  }, [analise]);

  const cadastroMensal: any[] = analise?.cadastro_mensal || [];
  const ultimoCadastro = cadastroMensal.length > 0 ? cadastroMensal[cadastroMensal.length - 1] : null;

  const clearFilters = () => {
    setFilterSetor(''); setFilterRisco(''); setFilterAlvara('');
    setFilterBairro(''); setFilterRegional(''); setFilterMedida('');
  };

  if (!municipioId) return <Alert severity="info">Selecione um municipio</Alert>;

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Georreferenciamento VISA</Typography>
          <Typography variant="body2" color="text.secondary">{municipioNome} — Setor Regulado</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1, mb: 0.3 }}>
              Mapa de Calor
            </Typography>
            <ToggleButtonGroup
              size="small"
              value={heatmapModo}
              exclusive
              onChange={(_, v) => { if (v !== null) setHeatmapModo(v); }}
              aria-label="Modo de mapa de calor"
            >
              <ToggleButton value="off" aria-label="Desligar"><LayersClearIcon fontSize="small" sx={{ mr: 0.5 }} /> Desligar</ToggleButton>
              <ToggleButton value="alto_risco" aria-label="Alto Risco"><WhatshotIcon fontSize="small" sx={{ mr: 0.5, color: '#E74C3C' }} /> Alto Risco</ToggleButton>
              <ToggleButton value="sem_licenca" aria-label="Sem Licenca"><NotListedLocationIcon fontSize="small" sx={{ mr: 0.5, color: '#2171B5' }} /> Sem Licenca</ToggleButton>
              <ToggleButton value="medidas" aria-label="Medidas Cautelares"><GavelIcon fontSize="small" sx={{ mr: 0.5, color: '#8E44AD' }} /> Medidas Cautelares</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Button
            variant="outlined" size="small" startIcon={<FilterListIcon />}
            endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={() => setShowFilters(!showFilters)}
            sx={{ alignSelf: 'flex-end' }}
          >
            Filtros
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Collapse in={showFilters}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6} sm={4} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Setor</InputLabel>
                <Select value={filterSetor} label="Setor" onChange={(e: SelectChangeEvent) => setFilterSetor(e.target.value)}>
                  <MenuItem value="">Todos</MenuItem>
                  {['Alimenticia','Farmacia','Saude','Estetica','Saneantes','Educacao','Outro'].map(s =>
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Risco</InputLabel>
                <Select value={filterRisco} label="Risco" onChange={(e: SelectChangeEvent) => setFilterRisco(e.target.value)}>
                  <MenuItem value="">Todos</MenuItem>
                  {['CRITICO','ALTO','MEDIO','BAIXO'].map(r =>
                    <MenuItem key={r} value={r}>{r}</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Alvara</InputLabel>
                <Select value={filterAlvara} label="Alvara" onChange={(e: SelectChangeEvent) => setFilterAlvara(e.target.value)}>
                  <MenuItem value="">Todos</MenuItem>
                  {['VIGENTE','VENCIDO','SUSPENSO','CASSADO','PENDENTE'].map(a =>
                    <MenuItem key={a} value={a}>{a}</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Regional</InputLabel>
                <Select value={filterRegional} label="Regional" onChange={(e: SelectChangeEvent) => setFilterRegional(e.target.value)}>
                  <MenuItem value="">Todas</MenuItem>
                  {REGIONAIS_CONTAGEM.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Bairro</InputLabel>
                <Select value={filterBairro} label="Bairro" onChange={(e: SelectChangeEvent) => setFilterBairro(e.target.value)}>
                  <MenuItem value="">Todos</MenuItem>
                  {bairros.map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Medida Cautelar</InputLabel>
                <Select value={filterMedida} label="Medida Cautelar" onChange={(e: SelectChangeEvent) => setFilterMedida(e.target.value)}>
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="true">Sim</MenuItem>
                  <MenuItem value="false">Nao</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Button variant="text" size="small" onClick={clearFilters}>Limpar</Button>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {loading ? (
          [1,2,3,4,5,6].map(i => <Grid item xs={6} sm={4} md={2} key={i}><Skeleton variant="rounded" height={100} /></Grid>)
        ) : (
          <>
            <Grid item xs={6} sm={4} md={2}>
              <KpiCard titulo="Estabelecimentos" valor={Number(kpis.total_estabelecimentos || 0)} subtitulo="Cadastrados" cor="#1E3A5F" icone={<LocationOnIcon />} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <KpiCard titulo="Cadastros no Mes" valor={Number(ultimoCadastro?.novos_cadastros || 0)}
                subtitulo={ultimoCadastro?.mes || '—'} cor="#2D7D9A" icone={<AddBusinessIcon />} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <KpiCard titulo="Alvaras Vencendo" valor={Number(kpis.total_vencendo_30d || 0)} subtitulo="Proximos 30 dias" cor="#F39C12" icone={<WarningIcon />} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <KpiCard titulo="Medidas Cautelares" valor={Number(kpis.total_medidas || 0)} subtitulo="Ativas" cor="#8E44AD" icone={<WarningIcon />} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <KpiCard titulo="Irregulares" valor={Number(kpis.total_irregulares || 0)} subtitulo="Irregular + Interditado" cor="#E74C3C" icone={<WarningIcon />} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <KpiCard titulo="Inspecoes Atrasadas" valor={Number(kpis.total_inspecoes_atrasadas || 0)} subtitulo="Vencidas" cor="#E67E22" icone={<WarningIcon />} />
            </Grid>
          </>
        )}
      </Grid>

      {!loading && cadastroMensal.length > 0 && (
        <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
            Evolucao do Cadastro de Estabelecimentos
          </Typography>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={cadastroMensal}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="mes" tick={{ fontSize: 10 }} />
              <YAxis yAxisId="left" tick={{ fontSize: 10 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} />
              <Tooltip />
              <Legend />
              <Line yAxisId="left" type="monotone" dataKey="novos_cadastros" name="Novos no mes" stroke="#2D7D9A" strokeWidth={2} dot={{ r: 3 }} />
              <Line yAxisId="right" type="monotone" dataKey="total_acumulado" name="Total acumulado" stroke="#1E3A5F" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* Map */}
      <Paper sx={{ mb: 2, overflow: 'hidden', borderRadius: 2 }}>
        {loading ? (
          <Skeleton variant="rounded" height={500} />
        ) : (
          <>
            <MapContainer center={mapCenter} zoom={13} scrollWheelZoom style={{ height: 500, width: '100%' }}>
              <TileLayer
                attribution={getTileAttribution()}
                url={getTileUrl()}
              />
              {comCoordenadas.map((e, idx) => (
                <Marker
                  key={`${e.id}-${idx}`}
                  position={[Number(e.latitude), Number(e.longitude)]}
                  icon={ICONS_RISCO[e.nivel_risco] || ICONS_RISCO.BAIXO}
                >
                  <LeafletTooltip direction="top" offset={[0, -22]} opacity={1}>
                    <div style={{ fontFamily: 'sans-serif', fontSize: 12, minWidth: 180 }}>
                      <div style={{ fontWeight: 700, marginBottom: 3 }}>{e.nome_fantasia || e.razao_social}</div>
                      {e.cnpj && <div>CNPJ: {e.cnpj}</div>}
                      <div>Atividade: {e.atividade_principal}</div>
                      <div>Alvara: {e.alvara_status || 'N/A'}{e.alvara_validade ? ` ate ${e.alvara_validade}` : ''}</div>
                      <div>Medida Cautelar: <b style={{ color: e.medida_cautelar ? '#E74C3C' : '#27AE60' }}>{e.medida_cautelar ? 'Sim' : 'Nao'}</b></div>
                    </div>
                  </LeafletTooltip>
                  <Popup>
                    <div style={{ minWidth: 280, fontFamily: 'sans-serif', fontSize: 13 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>{e.nome_fantasia || e.razao_social}</div>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <tbody>
                          {e.cnpj && <tr><td style={{ color: '#666', paddingRight: 8 }}>CNPJ</td><td>{e.cnpj}</td></tr>}
                          <tr><td style={{ color: '#666', paddingRight: 8 }}>Atividade</td><td>{e.atividade_principal}</td></tr>
                          <tr><td style={{ color: '#666', paddingRight: 8 }}>Setor</td><td>{e.setor}</td></tr>
                          <tr><td style={{ color: '#666', paddingRight: 8 }}>Bairro</td><td>{e.bairro}{e.regional ? ` (${e.regional})` : ''}</td></tr>
                          <tr>
                            <td style={{ color: '#666', paddingRight: 8 }}>Risco</td>
                            <td><span style={{ background: CORES_RISCO[e.nivel_risco] || '#999', color: '#fff', padding: '1px 8px', borderRadius: 4, fontSize: 11 }}>{e.nivel_risco}</span></td>
                          </tr>
                          <tr>
                            <td style={{ color: '#666', paddingRight: 8 }}>Situacao</td>
                            <td><span style={{ background: CORES_SITUACAO[e.situacao] || '#999', color: '#fff', padding: '1px 8px', borderRadius: 4, fontSize: 11 }}>{e.situacao}</span></td>
                          </tr>
                          <tr><td style={{ color: '#666', paddingRight: 8 }}>Alvara</td><td>{e.alvara_status || 'N/A'} {e.alvara_validade ? `(val: ${e.alvara_validade})` : ''}</td></tr>
                          {e.medida_cautelar && <tr><td style={{ color: '#666', paddingRight: 8 }}>Medida</td><td style={{ color: '#E74C3C', fontWeight: 600 }}>{e.medida_cautelar_tipo}</td></tr>}
                          {e.ultima_inspecao && <tr><td style={{ color: '#666', paddingRight: 8 }}>Ultima Inspecao</td><td>{e.ultima_inspecao}</td></tr>}
                        </tbody>
                      </table>
                      <Box sx={{ mt: 1.5, textAlign: 'center' }}>
                        <Button
                          size="small" variant="contained" startIcon={<OpenInNewIcon />}
                          onClick={() => setRegistroAbertoId(e.id)}
                          sx={{ textTransform: 'none', bgcolor: '#1E3A5F', '&:hover': { bgcolor: '#2D7D9A' } }}
                        >
                          Registro Integrado
                        </Button>
                      </Box>
                    </div>
                  </Popup>
                </Marker>
              ))}
              {heatmapModo !== 'off' && heatmapData.length > 0 && <HeatmapLayer points={heatmapData} modo={heatmapModo} />}
            </MapContainer>
            {/* Legend */}
            <Box sx={{ display: 'flex', gap: 2, p: 1.5, justifyContent: 'center', flexWrap: 'wrap', bgcolor: '#fafafa' }}>
              {Object.entries(CORES_RISCO).map(([label, color]) => (
                <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 14, height: 14, bgcolor: color, borderRadius: '50%', border: '2px solid #fff', boxShadow: 1 }} />
                  <Typography variant="caption">{label}</Typography>
                </Box>
              ))}
              <Chip label={`${comCoordenadas.length} estabelecimentos`} size="small" variant="outlined" icon={<LocationOnIcon />} />
            </Box>
          </>
        )}
      </Paper>

      {/* Charts */}
      {!loading && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {/* Pie: Por Setor */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, height: 320 }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>Distribuicao por Setor</Typography>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={chartSetor} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ name, percent }: any) => `${name} ${((percent || 0) * 100).toFixed(0)}%`} labelLine={false} fontSize={11}>
                    {chartSetor.map((_, i) => <Cell key={i} fill={CORES_PIE[i % CORES_PIE.length]} />)}
                  </Pie>
                  <Tooltip  />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
          {/* Bar: Por Bairro (stacked by risk) */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, height: 320 }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>Top Bairros por Risco</Typography>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={chartBairro} layout="vertical" margin={{ left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" fontSize={11} />
                  <YAxis dataKey="bairro" type="category" width={100} fontSize={10} />
                  <Tooltip />
                  <Legend fontSize={11} />
                  <Bar dataKey="CRITICO" stackId="a" fill={CORES_RISCO.CRITICO} name="Critico" />
                  <Bar dataKey="ALTO" stackId="a" fill={CORES_RISCO.ALTO} name="Alto" />
                  <Bar dataKey="MEDIO" stackId="a" fill={CORES_RISCO.MEDIO} name="Medio" />
                  <Bar dataKey="BAIXO" stackId="a" fill={CORES_RISCO.BAIXO} name="Baixo" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
          {/* Donut: Por Situacao */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, height: 320 }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>Situacao dos Estabelecimentos</Typography>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={chartSituacao} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={90} label={({ name, percent }: any) => `${name} ${((percent || 0) * 100).toFixed(0)}%`} labelLine={false} fontSize={11}>
                    {chartSituacao.map((entry: any, i: number) => <Cell key={i} fill={CORES_SITUACAO[entry.name] || CORES_PIE[i % CORES_PIE.length]} />)}
                  </Pie>
                  <Tooltip  />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* RF-16.4: Analise de Risco Avancada */}
      {!loading && analiseAvancada && (
        <AnaliseRiscoSection analise={analiseAvancada} />
      )}

      {/* Alertas Table */}
      {!loading && alertas.length > 0 && (
        <Paper sx={{ mb: 2 }}>
          <Box sx={{ p: 2, borderBottom: '1px solid #eee' }}>
            <Typography variant="subtitle1" fontWeight={700}>Alertas de Conformidade ({alertas.length})</Typography>
          </Box>
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Estabelecimento</TableCell>
                  <TableCell>Bairro</TableCell>
                  <TableCell>Data Ref.</TableCell>
                  <TableCell>Risco</TableCell>
                  <TableCell>Situacao</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {alertas.map((a, i) => (
                  <TableRow key={i} hover>
                    <TableCell>
                      <Chip
                        label={ALERTA_LABELS[a.tipo_alerta] || a.tipo_alerta}
                        size="small"
                        sx={{ bgcolor: ALERTA_COLORS[a.tipo_alerta] || '#999', color: '#fff', fontWeight: 600, fontSize: 11 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{a.nome_fantasia || a.razao_social}</Typography>
                      {a.nome_fantasia && <Typography variant="caption" color="text.secondary">{a.razao_social}</Typography>}
                    </TableCell>
                    <TableCell>{a.bairro}</TableCell>
                    <TableCell>{a.data_referencia || '-'}</TableCell>
                    <TableCell>
                      <Chip label={a.nivel_risco} size="small" sx={{ bgcolor: CORES_RISCO[a.nivel_risco] || '#999', color: '#fff', fontSize: 10 }} />
                    </TableCell>
                    <TableCell>
                      <Chip label={a.situacao} size="small" sx={{ bgcolor: CORES_SITUACAO[a.situacao] || '#999', color: '#fff', fontSize: 10 }} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* IA */}
      {!loading && (
        <AnalisePredicaoCard
          contexto="dashboard"
          municipioId={municipioId}
          painelId="visa-geo"
          dadosContexto={JSON.stringify({
            total_estabelecimentos: kpis.total_estabelecimentos,
            total_irregulares: kpis.total_irregulares,
            total_medidas: kpis.total_medidas,
            total_alvaras_vencidos: kpis.total_alvaras_vencidos,
            total_alertas: alertas.length,
            municipio: municipioNome,
          })}
        />
      )}

      <ChatIAButton contexto="dashboard" />

      {/* RF-16.2: Modal Registro Integrado */}
      <RegistroIntegradoModal
        open={registroAbertoId !== null}
        onClose={() => setRegistroAbertoId(null)}
        municipioId={municipioId}
        estabelecimentoId={registroAbertoId}
      />
    </Box>
  );
};

export default MonitoramentoVisaGeo;
